import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Mail,
  Calendar,
  TrendingUp,
  Search,
  Download,
  RefreshCw,
  UserCheck,
  ExternalLink,
  Trash2
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { toast } from '@/components/ui/use-toast';

interface WaitlistEntry {
  id: string;
  email: string;
  name: string;
  website_url: string;
  status: 'pending' | 'approved' | 'active' | 'rejected';
  login_attempts: number;
  last_login_attempt: string | null;
  created_at: string;
  google_profile_picture?: string;
}

interface WaitlistStats {
  total_waitlist: number;
  pending: number;
  approved: number;
  active: number;
  last_24h: number;
  last_7d: number;
  total_attempts: number;
}

export default function WaitlistDashboard() {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [stats, setStats] = useState<WaitlistStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'active'>('all');
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  const fetchWaitlistData = async () => {
    setLoading(true);
    try {
      const response = await authFetch('/api/admin/waitlist');
      const data = await response.json();
      setEntries(data.entries || []);
      setStats(data.stats || null);
    } catch (error) {
      console.error('Error fetching waitlist:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch waitlist data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWaitlistData();
  }, []);

  const approveUser = async (email: string) => {
    try {
      const response = await authFetch('/api/admin/waitlist/approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: `${email} has been approved`,
        });
        fetchWaitlistData();
      }
    } catch (error) {
      console.error('Error approving user:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve user',
        variant: 'destructive'
      });
    }
  };

  const deleteEntry = async (id: string, email: string, websiteUrl: string) => {
    if (!confirm(`Are you sure you want to delete the waitlist entry for ${email} (${websiteUrl})?`)) {
      return;
    }

    try {
      const response = await authFetch(`/api/admin/waitlist/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: `Waitlist entry for ${email} has been deleted`,
        });
        fetchWaitlistData();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete entry',
        variant: 'destructive'
      });
    }
  };

  const exportWaitlist = () => {
    const csv = [
      ['Email', 'Name', 'Website', 'Status', 'Login Attempts', 'Created At', 'Last Attempt'],
      ...entries.map(e => [
        e.email,
        e.name,
        e.website_url,
        e.status,
        e.login_attempts.toString(),
        new Date(e.created_at).toLocaleDateString(),
        e.last_login_attempt ? new Date(e.last_login_attempt).toLocaleDateString() : ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pagemind-waitlist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = entry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.website_url?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || entry.status === filter;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: 'default' | 'secondary' | 'destructive' | 'outline' } = {
      pending: 'secondary',
      approved: 'default',
      active: 'default',
      rejected: 'destructive'
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  const handleImageError = useCallback((email: string) => {
    setImageErrors(prev => new Set(prev).add(email));
  }, []);

  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Private Beta Waitlist</h1>
          <p className="text-gray-500">Manage users waiting for PageMind access</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportWaitlist} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={fetchWaitlistData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Waitlist</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_waitlist}</div>
              <p className="text-xs text-muted-foreground">
                {stats.pending} pending approval
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Last 24 Hours</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.last_24h}</div>
              <p className="text-xs text-muted-foreground">
                New signups today
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Last 7 Days</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.last_7d}</div>
              <p className="text-xs text-muted-foreground">
                New signups this week
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Attempts</CardTitle>
              <RefreshCw className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_attempts}</div>
              <p className="text-xs text-muted-foreground">
                Login attempts tracked
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1 max-w-sm">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email, name, or website..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
        <div className="flex gap-2">
          {(['all', 'pending', 'approved', 'active'] as const).map((status) => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Waitlist Table */}
      <Card>
        <CardHeader>
          <CardTitle>Waitlist Entries</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Website</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Last Attempt</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    No entries found
                  </TableCell>
                </TableRow>
              ) : (
                filteredEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {entry.google_profile_picture && !imageErrors.has(entry.email) ? (
                          <img
                            src={entry.google_profile_picture}
                            alt={entry.name}
                            className="w-8 h-8 rounded-full object-cover"
                            onError={() => handleImageError(entry.email)}
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                            {getInitials(entry.name)}
                          </div>
                        )}
                        <div>
                          <div className="font-medium">{entry.name}</div>
                          <div className="text-sm text-gray-500">{entry.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {entry.website_url && entry.website_url !== 'To be provided' ? (
                        <a
                          href={entry.website_url.startsWith('http') ? entry.website_url : `https://${entry.website_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          {entry.website_url}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-gray-400">Not provided</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(entry.status)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{entry.login_attempts}</Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(entry.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {entry.last_login_attempt
                        ? new Date(entry.last_login_attempt).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {entry.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => approveUser(entry.email)}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`mailto:${entry.email}`, '_blank')}
                          title="Send email"
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteEntry(entry.id, entry.email, entry.website_url)}
                          title="Delete entry"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}